export type SandboxLoyaltyOrigin = 'preset' | 'runtime';

export interface SandboxDiscount {
  __typename: 'Discount';
  origin: SandboxLoyaltyOrigin;
  id: number;
  title: string;
  value: number;
  entity: 'GOOD' | 'SERVICE' | 'TARIFF';
  is_tracked: boolean;
  dont_use_on_holidays: boolean;
  schedules: Array<{ start: number; end: number }>;
  active: boolean;
}

export interface SandboxCashback {
  __typename: 'DepositCashback';
  origin: SandboxLoyaltyOrigin;
  id: number;
  amount: number;
  value: number;
  is_percent: boolean;
  is_active: boolean;
  starts_at?: string;
  expires_at?: string;
}

export interface SandboxAchievement {
  __typename: 'Achievement';
  origin: SandboxLoyaltyOrigin;
  id: number;
  name: string;
  created_at: string;
  assigned_count: number;
  conditions: Array<{ name: string; value: number }>;
  rewards: Array<{ name: string; value: number }>;
}

export interface SandboxPromoClientScope {
  __typename: 'ScopedClient' | 'ScopedGroup';
  id: string;
  avatar_url?: string | null;
  phone?: string;
  title?: string;
}

export interface SandboxPromoValue {
  currency: { alias: 'RUB'; letter: '₽' };
  type: 'DISCOUNT' | 'DEPOSIT' | 'BONUS';
  value: number;
}

export interface SandboxPromoCode {
  __typename: 'PromoCodeEntity';
  origin: SandboxLoyaltyOrigin;
  active: boolean;
  active_from: string | null;
  active_to: string | null;
  amount: number;
  counters: { used: number };
  applicable_params: string;
  client_scope: SandboxPromoClientScope[] | null;
  code: string;
  created_at: string;
  id: number;
  is_tracked: boolean;
  scopes: Array<'SHELL' | 'MOBILE_APP' | 'BILLING'>;
  value: SandboxPromoValue;
}

export interface SandboxPromoCampaign {
  __typename: 'PromoCampaign';
  origin: SandboxLoyaltyOrigin;
  id: string;
  title: string;
  description: string;
  value: SandboxPromoValue;
  applicable_params: string;
  client_scope: SandboxPromoClientScope[];
  scopes: Array<'SHELL' | 'MOBILE_APP' | 'BILLING'>;
  generation_settings: {
    length: number;
    prefix: string;
    type: 'SYMBOLS' | 'NUMERIC' | 'MIXED';
  };
  counters: { total: number; used: number };
  active: boolean;
  active_from: string | null;
  active_to: string | null;
  is_tracked: boolean;
  created_at: string;
  updated_at: string;
}

export interface SandboxLoyaltySeed {
  discounts: SandboxDiscount[];
  cashbacks: SandboxCashback[];
  achievements: SandboxAchievement[];
  promoCodes: SandboxPromoCode[];
  promoCampaigns: SandboxPromoCampaign[];
}

const allApplicable = JSON.stringify({
  goods: { all: true, items: [] },
  services: { all: true, items: [] },
  tariffs: { all: true, items: [] },
});

const onlyApplicable = (
  type: 'goods' | 'services' | 'tariffs',
  items: Array<{ id: number; title: string }>
): string =>
  JSON.stringify({
    goods: { all: false, items: type === 'goods' ? items : [] },
    services: { all: false, items: type === 'services' ? items : [] },
    tariffs: { all: false, items: type === 'tariffs' ? items : [] },
  });

const schedule = (
  days: number[],
  startHour: number,
  endHour: number
): Array<{ start: number; end: number }> =>
  days.map((day) => ({
    start: (day - 1) * 86400 + startHour * 3600 + 1,
    end: (day - 1) * 86400 + endHour * 3600,
  }));

const iso = (anchorMs: number, days: number): string =>
  new Date(anchorMs + days * 86400000).toISOString();

const rubleValue = (
  type: SandboxPromoValue['type'],
  value: number
): SandboxPromoValue => ({
  currency: { alias: 'RUB', letter: '₽' },
  type,
  value,
});

export const createLoyaltySeed = (anchorMs: number): SandboxLoyaltySeed => ({
  discounts: [
    {
      __typename: 'Discount',
      origin: 'preset',
      id: 101,
      title: 'Будни до 16:00',
      value: 10,
      entity: 'TARIFF',
      is_tracked: false,
      dont_use_on_holidays: true,
      schedules: schedule([1, 2, 3, 4, 5], 10, 16),
      active: true,
    },
    {
      __typename: 'Discount',
      origin: 'preset',
      id: 102,
      title: 'Студенческая',
      value: 15,
      entity: 'TARIFF',
      is_tracked: false,
      dont_use_on_holidays: false,
      schedules: schedule([1, 2, 3, 4, 5], 12, 22),
      active: true,
    },
    {
      __typename: 'Discount',
      origin: 'preset',
      id: 103,
      title: 'Ночной гость',
      value: 20,
      entity: 'TARIFF',
      is_tracked: false,
      dont_use_on_holidays: false,
      schedules: schedule([5, 6, 7], 22, 24),
      active: true,
    },
    {
      __typename: 'Discount',
      origin: 'preset',
      id: 104,
      title: 'День рождения',
      value: 25,
      entity: 'SERVICE',
      is_tracked: false,
      dont_use_on_holidays: false,
      schedules: [],
      active: false,
    },
  ],
  cashbacks: [
    {
      __typename: 'DepositCashback',
      origin: 'preset',
      id: 201,
      amount: 1000,
      value: 5,
      is_percent: true,
      is_active: true,
      starts_at: iso(anchorMs, -45),
    },
    {
      __typename: 'DepositCashback',
      origin: 'preset',
      id: 202,
      amount: 2500,
      value: 10,
      is_percent: true,
      is_active: true,
      starts_at: iso(anchorMs, -30),
      expires_at: iso(anchorMs, 60),
    },
    {
      __typename: 'DepositCashback',
      origin: 'preset',
      id: 203,
      amount: 5000,
      value: 700,
      is_percent: false,
      is_active: false,
      starts_at: iso(anchorMs, -90),
      expires_at: iso(anchorMs, -7),
    },
  ],
  achievements: [
    {
      __typename: 'Achievement',
      origin: 'preset',
      id: 301,
      name: 'Добро пожаловать',
      created_at: iso(anchorMs, -120),
      assigned_count: 184,
      conditions: [{ name: 'register', value: 0 }],
      rewards: [{ name: 'add_deposit', value: 100 }],
    },
    {
      __typename: 'Achievement',
      origin: 'preset',
      id: 302,
      name: 'Постоянный гость',
      created_at: iso(anchorMs, -90),
      assigned_count: 46,
      conditions: [{ name: 'spent_hours', value: 360000 }],
      rewards: [{ name: 'add_discount', value: 5 }],
    },
    {
      __typename: 'Achievement',
      origin: 'preset',
      id: 303,
      name: 'Бронирую заранее',
      created_at: iso(anchorMs, -60),
      assigned_count: 71,
      conditions: [{ name: 'online_booking', value: 3 }],
      rewards: [{ name: 'add_deposit', value: 150 }],
    },
    {
      __typename: 'Achievement',
      origin: 'preset',
      id: 304,
      name: 'Делюсь впечатлением',
      created_at: iso(anchorMs, -40),
      assigned_count: 29,
      conditions: [{ name: 'club_comment', value: 0 }],
      rewards: [{ name: 'add_deposit', value: 50 }],
    },
  ],
  promoCodes: [
    {
      __typename: 'PromoCodeEntity',
      origin: 'preset',
      id: 401,
      code: 'WELCOME500',
      amount: 100,
      counters: { used: 27 },
      value: rubleValue('DEPOSIT', 500),
      applicable_params: allApplicable,
      client_scope: null,
      scopes: ['SHELL', 'MOBILE_APP'],
      active: true,
      active_from: iso(anchorMs, -30),
      active_to: iso(anchorMs, 90),
      is_tracked: false,
      created_at: iso(anchorMs, -35),
    },
    {
      __typename: 'PromoCodeEntity',
      origin: 'preset',
      id: 402,
      code: 'NIGHT20',
      amount: 200,
      counters: { used: 84 },
      value: rubleValue('DISCOUNT', 20),
      applicable_params: onlyApplicable('tariffs', [
        { id: 1, title: 'Ночной пакет' },
      ]),
      client_scope: null,
      scopes: ['BILLING'],
      active: true,
      active_from: iso(anchorMs, -14),
      active_to: iso(anchorMs, 45),
      is_tracked: false,
      created_at: iso(anchorMs, -20),
    },
    {
      __typename: 'PromoCodeEntity',
      origin: 'preset',
      id: 403,
      code: 'SNACK10',
      amount: 150,
      counters: { used: 38 },
      value: rubleValue('DISCOUNT', 10),
      applicable_params: onlyApplicable('goods', [
        { id: 1, title: 'Снеки' },
        { id: 2, title: 'Напитки' },
      ]),
      client_scope: null,
      scopes: ['BILLING'],
      active: true,
      active_from: iso(anchorMs, -7),
      active_to: iso(anchorMs, 30),
      is_tracked: false,
      created_at: iso(anchorMs, -10),
    },
    {
      __typename: 'PromoCodeEntity',
      origin: 'preset',
      id: 404,
      code: 'BONUS300',
      amount: 80,
      counters: { used: 80 },
      value: rubleValue('BONUS', 300),
      applicable_params: allApplicable,
      client_scope: null,
      scopes: ['SHELL', 'MOBILE_APP'],
      active: false,
      active_from: iso(anchorMs, -90),
      active_to: iso(anchorMs, -5),
      is_tracked: false,
      created_at: iso(anchorMs, -95),
    },
  ],
  promoCampaigns: [
    {
      __typename: 'PromoCampaign',
      origin: 'preset',
      id: 'preset:promo-campaign:summer',
      title: 'Летняя лига',
      description: 'Бонус участникам еженедельной клубной лиги',
      value: rubleValue('BONUS', 250),
      applicable_params: allApplicable,
      client_scope: [
        { __typename: 'ScopedGroup', id: 'newcomers', title: 'Новички' },
      ],
      scopes: ['SHELL', 'MOBILE_APP'],
      generation_settings: { length: 8, prefix: 'LIGA', type: 'MIXED' },
      counters: { total: 120, used: 43 },
      active: true,
      active_from: iso(anchorMs, -15),
      active_to: iso(anchorMs, 60),
      is_tracked: false,
      created_at: iso(anchorMs, -20),
      updated_at: iso(anchorMs, -2),
    },
    {
      __typename: 'PromoCampaign',
      origin: 'preset',
      id: 'preset:promo-campaign:birthday',
      title: 'День рождения клуба',
      description: 'Скидка постоянным гостям в праздничную неделю',
      value: rubleValue('DISCOUNT', 15),
      applicable_params: allApplicable,
      client_scope: [
        {
          __typename: 'ScopedGroup',
          id: 'regulars',
          title: 'Постоянные гости',
        },
      ],
      scopes: ['BILLING'],
      generation_settings: { length: 6, prefix: 'BDAY', type: 'MIXED' },
      counters: { total: 75, used: 19 },
      active: true,
      active_from: iso(anchorMs, -3),
      active_to: iso(anchorMs, 7),
      is_tracked: false,
      created_at: iso(anchorMs, -10),
      updated_at: iso(anchorMs, -1),
    },
  ],
});
